import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  AddOutlined,
  CalendarMonthOutlined,
  DeleteOutline,
  Edit,
  FileDownloadOutlined,
  FilterAltOutlined,
  Groups2Outlined,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  KeyboardArrowDownOutlined,
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
  SearchOutlined,
  Star,
  StarBorderOutlined,
} from "@mui/icons-material";
import SearchSelect from "components/atoms/SearchSelect/SearchSelect";
import SelectField from "components/atoms/SelectField/SelectField";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import { useAcao, AcaoOrder } from "./hooks/useAcao";
import { useAuth } from "../../../resources/AuthProvider";
import { AcaoQualificacao } from "../../../types/acao";
import { formatarDataDbParaBr } from "../../../utils/Masks";
import styles from "./AcoesQualificacao.module.css";
import stylesInput from "../../../styles/Input.module.css";
import stylesGrid from "../../../styles/Grid.module.css";
import stylesButton from "../../../styles/Button.module.css";

const situationLabels: Record<string, string> = {
  em_construcao: "Em construção",
  inscricoes_abertas: "Inscrições abertas",
  aguardando_inicio: "Aguardando início",
  em_andamento: "Em andamento",
  planejada: "Planejada",
  concluida: "Concluída",
  encerrada: "Encerrada",
  cancelada: "Cancelada",
};

const orderOptions: Array<{ value: AcaoOrder; label: string }> = [
  { value: "mais_recentes", label: "Mais recentes" },
  { value: "mais_antigas", label: "Mais antigas" },
  { value: "nome_az", label: "Nome (A-Z)" },
  { value: "nome_za", label: "Nome (Z-A)" },
  { value: "data_inicio", label: "Data de início" },
  { value: "data_termino", label: "Data de término" },
];

const formatSituation = (situation?: string) => situationLabels[situation ?? ""] ?? "Não informada";

const exportActions = (actions: AcaoQualificacao[]) => {
  const headers = ["Código", "Ação", "Tipologia", "Modalidade", "Início", "Fim", "Situação"];
  const rows = actions.map((action) => [
    action.id,
    action.nome,
    action.tipologia,
    action.modalidade,
    formatarDataDbParaBr(action.data_inicio),
    formatarDataDbParaBr(action.data_fim),
    formatSituation(action.situacao),
  ]);
  const csv = [headers, ...rows]
    .map((row) => row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(";"))
    .join("\r\n");
  const url = URL.createObjectURL(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "acoes-qualificacao.csv";
  link.click();
  URL.revokeObjectURL(url);
};

const ListarAcoesSkeleton = () => (
  <main className={styles.page} aria-busy="true" aria-label="Carregando ações de qualificação">
    <div className={styles.pageHeader}><Skeleton variant="text" width={290} height={40} /></div>
    <section className={styles.filterPanel}>
      <div className={stylesGrid.grid12}>
        {Array.from({ length: 7 }, (_, index) => <div key={index} className={`${styles.filterField} ${index === 0 ? stylesGrid.inputGroup4 : stylesGrid.inputGroup2}`}><Skeleton variant="text" width={90} /><Skeleton variant="rounded" height={40} /></div>)}
        <div className={`${styles.filterActions} ${stylesGrid.inputGroup3}`}><Skeleton variant="text" width={110} /><div><Skeleton variant="rounded" width={78} height={34} /><Skeleton variant="rounded" width={78} height={34} /></div></div>
      </div>
    </section>
    <div className={styles.listToolbar}><Skeleton variant="rounded" width={240} height={34} /><Skeleton variant="rounded" width={210} height={38} /></div>
    <section className={styles.workspace}>
      <aside className={styles.refinePanel}>
        <Skeleton variant="text" width={150} height={26} />
        {Array.from({ length: 3 }, (_, index) => <div key={index} className={styles.refineGroup}><Skeleton variant="text" width={95} />{Array.from({ length: index === 0 ? 5 : 3 }, (_, row) => <Skeleton key={row} variant="text" width={row % 2 ? "72%" : "88%"} height={28} />)}</div>)}
      </aside>
      <div className={styles.resultsPanel}>
        <div className={styles.resultsHeader}><Skeleton variant="rounded" width={190} height={38} /><Skeleton variant="rounded" width={185} height={36} /></div>
        <div className={styles.tableContainer}>{Array.from({ length: 8 }, (_, index) => <Skeleton key={index} variant="rectangular" height={43} className={styles.tableSkeletonRow} />)}</div>
        <div className={styles.pagination}><Skeleton variant="rounded" width={160} height={34} /><Skeleton variant="rounded" width={190} height={34} /></div>
      </div>
    </section>
  </main>
);

const ListarAcoes = () => {
  const navigate = useNavigate();
  const { user, hasActionPermission } = useAuth();
  const canCreateAcao = hasActionPermission("acoes.create");
  const canEditAcaoAll = hasActionPermission("acoes.update_all");
  const canEditAcaoOwn = hasActionPermission("acoes.update_own");
  const canDeleteAcaoAll = hasActionPermission("acoes.delete_all");
  const canDeleteAcaoOwn = hasActionPermission("acoes.delete_own");
  const canManageTurmas = hasActionPermission("turmas.view_all") || hasActionPermission("turmas.view_own");
  const showActionsColumn = canEditAcaoAll || canEditAcaoOwn || canDeleteAcaoAll || canDeleteAcaoOwn || canManageTurmas;
  const [openInativar, setOpenInativar] = useState(false);
  const [selectedAcaoId, setSelectedAcaoId] = useState<string | null>(null);
  const [showAllYears, setShowAllYears] = useState(false);

  const {
    loading, pageLoading, msgError, msgSuccess, loadingConfirm, acoes, tipologias, modalidades, temas,
    currentPage, perPage, totalPages, totalItems, rangeStart, rangeEnd, filtros, anos,
    situacoes, ofertaCounts, activeTab, ordenacao, selectedYear, selectedSituacao,
    selectedTipoOferta, setCurrentPage, setPerPage, atualizarFiltro, pesquisar,
    limparFiltros, changeTab, selectYear, toggleSituacao, toggleTipoOferta,
    changeOrdenacao, inativarAcao, toggleFavorita,
  } = useAcao();

  const handleInativar = async () => {
    if (!selectedAcaoId) return;
    const wasInactivated = await inativarAcao(selectedAcaoId);
    if (wasInactivated) {
      setOpenInativar(false);
      setSelectedAcaoId(null);
    }
  };

  if (pageLoading) return <ListarAcoesSkeleton />;

  return (
    <main className={styles.page}>
      <div className={styles.pageHeader}>
        <h1>Ações de Qualificação</h1>
      </div>

      <section className={styles.filterPanel} aria-label="Filtros de ações">
        <div className={stylesGrid.grid12}>
          <div className={`${styles.filterField} ${stylesGrid.inputGroup4}`}>
            <label>Nome da ação</label>
            <TextField
              size="small"
              placeholder="Digite o nome da ação"
              variant="standard"
              fullWidth
              value={filtros.searchNome}
              onChange={(event) => atualizarFiltro("searchNome", event.target.value)}
              inputProps={{ maxLength: 150 }}
              InputProps={{
                className: `${stylesInput.inputSoft} ${styles.actionSearchInput}`,
                disableUnderline: true,
                endAdornment: <SearchOutlined fontSize="small" />,
              }}
            />
          </div>
          <div className={`${styles.filterField} ${stylesGrid.inputGroup3}`}>
            <label>Tipo de ação</label>
            <SearchSelect label="" placeholder="Selecione" options={tipologias} value={filtros.searchTipologia}
              onChange={(value) => atualizarFiltro("searchTipologia", value)} paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper} labelClassname={stylesInput.labelSelect} inputClassname={stylesInput.inputSoft} />
          </div>
          <div className={`${styles.filterField} ${stylesGrid.inputGroup3}`}>
            <label>Tipologia</label>
            <SearchSelect label="" placeholder="Selecione" options={tipologias} value={filtros.searchTipologia}
              onChange={(value) => atualizarFiltro("searchTipologia", value)} paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper} labelClassname={stylesInput.labelSelect} inputClassname={stylesInput.inputSoft} />
          </div>
          <div className={`${styles.filterField} ${stylesGrid.inputGroup2}`}>
            <label>Tema</label>
            <SearchSelect label="" placeholder="Selecione" options={temas} value={filtros.tema}
              onChange={(value) => atualizarFiltro("tema", value)} paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper} labelClassname={stylesInput.labelSelect} inputClassname={stylesInput.inputSoft} />
          </div>
          <div className={`${styles.filterField} ${stylesGrid.inputGroup3}`}>
            <label>Modalidade da ação</label>
            <SearchSelect label="" placeholder="Selecione" options={modalidades} value={filtros.searchModalidade}
              onChange={(value) => atualizarFiltro("searchModalidade", value)} paperClassname={stylesInput.paper}
              popperClassname={stylesInput.popper} labelClassname={stylesInput.labelSelect} inputClassname={stylesInput.inputSoft} />
          </div>
          <div className={`${styles.filterField} ${stylesGrid.inputGroup3}`}>
            <label>Período de início</label>
            <TextField size="small" type="date" variant="standard" fullWidth value={filtros.searchDataInicio}
              onChange={(event) => atualizarFiltro("searchDataInicio", event.target.value)}
              InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }} />
          </div>
          <div className={`${styles.filterField} ${stylesGrid.inputGroup3}`}>
            <label>Período de fim</label>
            <TextField size="small" type="date" variant="standard" fullWidth value={filtros.searchDataFim}
              onChange={(event) => atualizarFiltro("searchDataFim", event.target.value)}
              InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }} />
          </div>
          <div className={`${styles.filterActions} ${stylesGrid.inputGroup3}`}>
            <Button variant="text" className={stylesButton.buttonSmallInlineLink} startIcon={<FilterAltOutlined />}>Filtros avançados</Button>
            <div>
              <Button variant="outlined" className={stylesButton.buttonSmallSoftMuted} onClick={limparFiltros}>Limpar</Button>
              <Button variant="contained" className={stylesButton.buttonSmallSoftPrimary} onClick={pesquisar}>Filtrar</Button>
            </div>
          </div>
        </div>
      </section>

      <div className={styles.listToolbar}>
        <div className={styles.tabs} role="tablist" aria-label="Visualização de ações">
          {([
            ["todas", "Todas as ações"],
            ["por-ano", "Por ano"],
            ["favoritas", "Favoritas"],
          ] as const).map(([value, label]) => (
            <button key={value} type="button" role="tab" aria-selected={activeTab === value}
              className={activeTab === value ? styles.activeTab : styles.tab} onClick={() => changeTab(value)}>
              {label}
            </button>
          ))}
        </div>
        <div className={styles.toolbarActions}>
          <Button variant="outlined" className={stylesButton.buttonSoftOutline} startIcon={<FileDownloadOutlined />} onClick={() => exportActions(acoes)}>
            Exportar
          </Button>
          {canCreateAcao && (
            <Button variant="contained" className={stylesButton.buttonSoftPrimary} startIcon={<AddOutlined />} onClick={() => navigate("/nova-acao")}>
              Nova ação
            </Button>
          )}
        </div>
      </div>

      {(msgError || msgSuccess) && <div className={styles.message}>{msgError && <Alert severity="error">{msgError}</Alert>}{msgSuccess && <Alert severity="success">{msgSuccess}</Alert>}</div>}

      <section className={styles.workspace}>
        <aside className={styles.refinePanel}>
          <div className={styles.refineHeader}>
            <h2>Refinar resultados</h2>
            <button type="button" onClick={limparFiltros}>
              Limpar filtros <DeleteOutline fontSize="inherit" />
            </button>
          </div>
          <div className={styles.refineGroup}>
            <h3>Ano</h3>
            {anos.slice(0, showAllYears ? anos.length : 5).map((item) => (
              <label
                key={item.ano}
                className={`${styles.refineOption} ${selectedYear === item.ano ? styles.refineOptionSelected : ""}`}
              >
                <span className={styles.refineOptionLabel}>
                  <Checkbox checked={selectedYear === item.ano} onChange={() => selectYear(item.ano)} size="small" />
                  <span>{item.ano}</span>
                </span>
                <strong>{item.total_acoes}</strong>
              </label>
            ))}
            {anos.length > 5 && (
              <button type="button" className={styles.refineMoreButton} onClick={() => setShowAllYears((current) => !current)}>
                {showAllYears ? "Ver menos" : "Ver mais"} <KeyboardArrowDownOutlined fontSize="inherit" />
              </button>
            )}
          </div>
          <div className={styles.refineGroup}>
            <h3>Situação</h3>
            {situacoes.map((item) => (
              <label key={item.situacao} className={styles.refineOption}>
                <span className={styles.refineOptionLabel}>
                  <Checkbox checked={selectedSituacao === item.situacao} onChange={() => toggleSituacao(item.situacao)} size="small" />
                  <i className={`${styles.statusDot} ${styles[`dot_${item.situacao}`] ?? ""}`} />
                  <span>{formatSituation(item.situacao)}</span>
                </span>
                <strong>{item.total_acoes}</strong>
              </label>
            ))}
          </div>
          <div className={styles.refineGroup}>
            <h3>Responsável técnico</h3>
            {[
              ["OFERTA_ESTADUAL", "Oferta Estadual"],
              ["OFERTA_EXTERNA", "Oferta Externa"],
            ].map(([value, label]) => (
              <label key={value} className={styles.refineOption}>
                <span className={styles.refineOptionLabel}>
                  <Checkbox checked={selectedTipoOferta === value} onChange={() => toggleTipoOferta(value)} size="small" />
                  <span>{label}</span>
                </span>
                <strong>{ofertaCounts[value as keyof typeof ofertaCounts]}</strong>
              </label>
            ))}
          </div>
        </aside>

        <div className={styles.resultsPanel}>
          <div className={styles.resultsHeader}>
            <div className={styles.resultCount}><CalendarMonthOutlined /><div><strong>{totalItems} ações encontradas</strong><span>Exibindo {rangeStart} a {rangeEnd} de {totalItems}</span></div></div>
            <div className={styles.orderControl}>
              <label htmlFor="action-order">Ordenar por</label>
              <TextField id="action-order" select size="small" value={ordenacao} onChange={(event) => changeOrdenacao(event.target.value as AcaoOrder)}>
                {orderOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField></div>
          </div>
          <TableContainer className={styles.tableContainer}>
            {loading ? <div className={styles.loading}><CircularProgress size={28} /></div> : (
              <Table size="small" className={styles.actionsTable}>
                <TableHead><TableRow>
                  <TableCell>Favorita</TableCell><TableCell>Ação</TableCell><TableCell>Tipologia</TableCell><TableCell>Modalidade</TableCell><TableCell>Período</TableCell><TableCell>Situação</TableCell>
                  {showActionsColumn && <TableCell align="center">Ações</TableCell>}
                </TableRow></TableHead>
                <TableBody>
                  {acoes.map((acao) => {
                    const isOwner = typeof acao.created_by === "number" && typeof user?.id === "number" && acao.created_by === user.id;
                    const canEdit = canEditAcaoAll || (canEditAcaoOwn && isOwner);
                    const canDelete = canDeleteAcaoAll || (canDeleteAcaoOwn && isOwner);
                    return <TableRow key={acao.id}>
                      <TableCell className={styles.favoriteCell}><Tooltip title={acao.favorita ? "Remover dos favoritos" : "Adicionar aos favoritos"} placement="top"><IconButton onClick={() => toggleFavorita(acao)} aria-label="Alternar favorita"><>{acao.favorita ? <Star className={styles.favoriteActive} /> : <StarBorderOutlined />}</></IconButton></Tooltip></TableCell>
                      <TableCell className={styles.actionName}><span className={styles.code}>#{acao.id}</span><div><strong>{acao.nome}</strong><small>{acao.tipologia}</small></div></TableCell>
                      <TableCell>{acao.tipologia}</TableCell><TableCell>{acao.modalidade}</TableCell>
                      <TableCell className={styles.period}>{formatarDataDbParaBr(acao.data_inicio)}<br />a {formatarDataDbParaBr(acao.data_fim)}</TableCell>
                      <TableCell><span className={`${styles.statusBadge} ${styles[`status_${acao.situacao}`] ?? ""}`}>{formatSituation(acao.situacao)}</span></TableCell>
                      {showActionsColumn && <TableCell><div className={styles.rowActions}>
                        {canEdit && <Tooltip title="Editar ação" placement="top"><IconButton size="small" onClick={() => navigate(`/editar-acao?id=${acao.id}`)}><Edit fontSize="small" /></IconButton></Tooltip>}
                        {canManageTurmas && <Tooltip title="Gerenciar turmas" placement="top"><IconButton size="small" onClick={() => navigate(`/gestao-turmas?id=${acao.id}`)}><Groups2Outlined fontSize="small" /></IconButton></Tooltip>}
                        {canDelete && <Tooltip title="Inativar ação" placement="top"><IconButton size="small" color="error" onClick={() => { setSelectedAcaoId(String(acao.id)); setOpenInativar(true); }}><DeleteOutline fontSize="small" /></IconButton></Tooltip>}
                      </div></TableCell>}
                    </TableRow>;
                  })}
                  {!acoes.length && <TableRow><TableCell className={styles.emptyState} colSpan={showActionsColumn ? 7 : 6}>Nenhuma ação encontrada para os filtros selecionados.</TableCell></TableRow>}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          <div className={styles.pagination}>
            <div className={styles.perPage}><span>Resultados por página</span><SelectField label="" value={String(perPage)} onChange={(value) => { setPerPage(Number(value)); setCurrentPage(1); }} options={[{ nome: "10" }, { nome: "20" }, { nome: "30" }]} inputClassName={stylesInput.inputSoft} disableClearable disableTyping /></div>
            <div className={styles.paginationControls}>
              <IconButton disabled={currentPage === 1} onClick={() => setCurrentPage(1)}><KeyboardDoubleArrowLeftOutlined /></IconButton>
              <IconButton disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}><KeyboardArrowLeftOutlined /></IconButton>
              <span>Página {currentPage} de {totalPages}</span>
              <IconButton disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}><KeyboardArrowRightOutlined /></IconButton>
              <IconButton disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}><KeyboardDoubleArrowRightOutlined /></IconButton>
            </div>
          </div>
        </div>
      </section>

      <ConfirmDialog open={openInativar} loading={loadingConfirm} onClose={() => setOpenInativar(false)} onConfirm={handleInativar}
        title="Inativar ação" message="Deseja realmente inativar esta ação?" titleLoadButton="Inativando..." titleButtonConfirm="Inativar" />
    </main>
  );
};

export default ListarAcoes;
